import type { IsoLocale } from '@repo/shared/locale';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox } from 'component/basics';
import { PaginationRow } from 'component/dashboard/pagination-row';
import type { TableColumn } from 'component/dashboard/table-grid';
import { TableGrid } from 'component/dashboard/table-grid';
import { SearchField } from 'component/form';
import { Heading } from 'component/text';
import { useSiloLanguage } from 'component/translation';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useSessionDataValues } from 'util/datastore';
import { type TLabel, t } from 'util/stdtypes';

/**
 * Decides whether a row survives the filter, given the filter's own current
 * session-data value. `sessionKey` names the key that value is read from, so
 * the table can subscribe on the filter's behalf; a filter that depends on no
 * session state omits it and is passed `undefined`.
 */
export type TableFilterFunc<Row> = (selected: unknown, item: Row) => boolean;
export type TableFilter<Row> = {
    key: string;
    widget: React.ComponentType;
    sessionKey?: string;
    filter: TableFilterFunc<Row>;
};

export type TableSearchFunc<Row> = (item: Row, searchTerm: string, locale: IsoLocale) => boolean;

export type TableListWidgetProps<Row> = {
    itemList: Row[];
    filteredItemList?: Row[];
    searchedItemList?: Row[];
};

export type PaginatedTableProps<Row> = {
    titleLabel: TLabel;
    columns: TableColumn<Row>[];
    itemList: Row[] | undefined;
    itemKey: (item: Row) => string;
    filters?: TableFilter<Row>[];
    searchFilter?: TableSearchFunc<Row>;
    rightWidgets?: React.ComponentType[];
    emptyWidget?: React.ComponentType<TableListWidgetProps<Row>>;
};

const PAGE_SIZE = 10;

/**
 * A titled table with filter widgets, an optional search box, and client-side
 * pagination (10 rows a page), which resets to the first page whenever the
 * search or filters change. `itemList` is `undefined` while loading.
 */
export function PaginatedTable<Row>({
    titleLabel,
    columns,
    itemList,
    itemKey,
    filters = [],
    searchFilter,
    rightWidgets = [],
    emptyWidget: EmptyWidget,
}: PaginatedTableProps<Row>) {
    const s = usePaginatedTableStyles();
    const language = useSiloLanguage();
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(0);

    const selectedByKey = useSessionDataValues(filters.flatMap((filter) => filter.sessionKey ?? []));

    const loading = itemList === undefined;
    const items = itemList ?? [];

    const filteredItemList = items.filter((item) =>
        filters.every((filter) => filter.filter(filter.sessionKey ? selectedByKey[filter.sessionKey] : undefined, item)),
    );
    const searchedItemList = searchFilter ? filteredItemList.filter((item) => searchFilter(item, search, language)) : filteredItemList;

    // Reset to first page when filters or search change
    useEffect(() => {
        setCurrentPage(0);
    }, [search, searchedItemList.length]);

    const paginatedItemList = searchedItemList.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

    return (
        <View style={s.table}>
            <HorizBox center spread>
                <Heading label={titleLabel} type='large' weight='medium' />
                {searchFilter && (
                    <SearchField onChange={setSearch} placeholderLabel={t('space.table.search', 'Search...')} size='small' value={search} />
                )}
            </HorizBox>
            <HorizBox center spread>
                <HorizBox gap={16}>
                    {filters.map((filter) => (
                        <filter.widget key={filter.key} />
                    ))}
                </HorizBox>
                <HorizBox>
                    {rightWidgets.map((widget, i) =>
                        // eslint-disable-next-line react/no-array-index-key
                        React.createElement(widget, { key: i }),
                    )}
                </HorizBox>
            </HorizBox>
            <TableGrid
                columns={columns}
                emptyWidget={
                    EmptyWidget && <EmptyWidget filteredItemList={filteredItemList} itemList={items} searchedItemList={searchedItemList} />
                }
                itemKey={itemKey}
                itemList={paginatedItemList}
                loading={loading}
            />
            {searchedItemList.length > PAGE_SIZE && (
                <PaginationRow
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    pageSize={PAGE_SIZE}
                    totalItems={searchedItemList.length}
                />
            )}
        </View>
    );
}

const usePaginatedTableStyles = createThemedStyles((tokens) => ({
    table: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
}));
